import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CARE_STAFF_ROLES,
  TEAM_ROLES,
  api,
  emergencyAcknowledge,
  emergencyAcknowledgeUndo,
  emergencyArrived,
  emergencyCalled911,
  emergencyEmsArrived,
  emergencyResolve,
  guardianAssign,
  guardianWatchAssign,
  guardianAssignable,
  guardianIncidents,
  guardianIncidentsNetwork,
  guardianOpenSignal,
  guardianSignals,
  guardianStep,
  guardianTape,
  guardianWatch,
  residentsRoster,
  type ApiClient,
  type ApiError,
  type GuardianIncident,
  type GuardianKind,
  type MeAssignment,
  type OpenSignalBody,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { ScopeEmpty, nothingFrom } from '../../ui/ScopeEmpty';
import { useNavBadges } from '../../shell/Shell';
import { useQuery } from '../../useQuery';
import { sx as designSx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { useReadOnly, useReadOnlyReason } from '../../ui/ReadOnly';
import { ErrorState, Skeleton } from '../../ui/states';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { EmergencyExtras } from './EmergencyExtras';
import { GuardianApprovals } from './Approvals';
import { GuardianFeedsView } from './GuardianFeeds';
import { medicineAlerts } from './medAlerts';
import { guardianFeeds } from './feedsVals';
import { tierLabel } from '../residents/common';
import { EM, NEXT_STEP, btn, buildGuardian, emergencyCard, familyOf, recipientsFor, type Case, type EmAct, type Filter, type Lens } from './model';
import './guardian.css';

// A port of the Console V9 design's Guardian (template L4184-4420, logic gdLayer L38678-38860
// and the wandering and alarm-noise block L42866-42915) over the live routes. The older tabbed Guardian in the design (gOld,
// T4098-4962) is switched off by the design itself (gdLayer forces gOld false), so it is
// not ported.

/**
 * The design loads Archivo from Google as the static weights 400 to 800, so its 750 renders
 * at 800. The console bundles the variable font, which would draw a true 750: map it.
 */
const sx = (css: string) => designSx(css.replace(/font-weight:750/g, 'font-weight:800'));

/** Poll the cases this often (the design ticks the clocks every second). */
export const GUARDIAN_POLL_MS = 15_000;

/** A proposed route the back end does not serve yet answers 404: treat it as empty. */
function optional<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((e: ApiError) => {
    if (e && e.kind === 'not_found') return null;
    throw e;
  });
}

function useNow(stepMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), stepMs);
    return () => window.clearInterval(t);
  }, [stepMs]);
  return now;
}

/** An anchor-styled button: the design draws these as links with its own inline style. */
function A({ style, onClick, children, disabled, title, label }: { style: string; onClick: () => void; children: ReactNode; disabled?: boolean; title?: string; label?: string }) {
  return (
    <button type="button" className="gd-a" style={sx(style)} onClick={onClick} disabled={disabled} aria-disabled={disabled} title={title} aria-label={label}>
      {children}
    </button>
  );
}

const REPORT_KINDS: [string, string][] = [
  ['abuse', 'Abuse'],
  ['neglect', 'Neglect'],
  ['money', 'Money'],
  ['injury', 'Injury, cause unknown'],
  ['other', 'Something else'],
];

/** "Report a concern" kinds in the server's words (kind + the concern). */
function reportBody(k: string): Pick<OpenSignalBody, 'kind' | 'concern'> {
  if (k === 'abuse') return { kind: 'abuse_neglect', concern: 'abuse' };
  if (k === 'neglect') return { kind: 'abuse_neglect', concern: 'neglect' };
  if (k === 'money') return { kind: 'scam' as GuardianKind };
  if (k === 'injury') return { kind: 'other', concern: 'injury' };
  return { kind: 'other' };
}

function lensOf(role: string | null): Lens {
  if (role === 'ADMIN') return 'admin';
  if (role && CARE_STAFF_ROLES.has(role as never)) return 'staff';
  return 'ro';
}

export function GuardianPage() {
  const { role, realRole } = useConsoleRole();
  const { client, session } = useSession();
  const readOnly = useReadOnly();
  const roReason = useReadOnlyReason();
  const lens = lensOf(role);
  const staff = lens === 'staff';
  // Aithora's own session (also while viewing as a facility role) reads every building.
  const team = !!realRole && TEAM_ROLES.has(realRole);
  const token = session?.token;
  const now = useNow(1000);
  const badges = useNavBadges();

  // The facility switcher (V6): Super Admin and Operator see the picked buildings' cases,
  // emergencies, watch list and tape. Building roles have no switcher (no pick is sent).
  const facilities = facilitiesParam(usePickedIds());
  const load = <T,>(fn: (c: ApiClient, s: AbortSignal) => Promise<T>, opts: { pollMs?: number; enabled?: boolean } = {}) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery<T>((s) => fn(client, s), [client, token, facilities], opts);

  const signals = load((c, signal) => guardianSignals(c, { query: { limit: 200, facilities }, signal }), { pollMs: GUARDIAN_POLL_MS });
  const incidents = load<{ incidents: GuardianIncident[] } | null>(
    (c, signal) => (team ? optional(guardianIncidentsNetwork(c, { query: facilities ? { facilities } : undefined, signal })) : guardianIncidents(c, { query: { recentDays: 7 }, signal })),
    { pollMs: GUARDIAN_POLL_MS },
  );
  const watch = load((c, signal) => optional(guardianWatch(c, { query: facilities ? { facilities } : undefined, signal })), { pollMs: GUARDIAN_POLL_MS });
  const tape = load((c, signal) => optional(guardianTape(c, { query: { limit: 12, facilities }, signal })), { pollMs: GUARDIAN_POLL_MS });
  const assignment = load<MeAssignment | null>((c, signal) => api.meAssignment(c, { signal }), { enabled: staff });
  const assignable = load((c, signal) => optional(guardianAssignable(c, { signal })), { enabled: lens === 'admin' });

  const [filter, setFilter] = useState<Filter>('open');
  // Deep link from an Atlas answer: ?case=<incident id> opens that case (src/aithora/v3/atlas/answer-links.ts),
  // also when Guardian is already open.
  const { search } = useLocation();
  const linked = new URLSearchParams(search).get('case');
  const [selId, setSel] = useState<string | null>(linked);
  useEffect(() => {
    if (linked) setSel(linked);
  }, [linked]);
  const [formFor, setFormFor] = useState<string | null>(null);
  const [formSbi, setFormSbi] = useState<'' | 'yes' | 'no' | 'unsure'>('');
  const [formRefs, setFormRefs] = useState<Record<string, string>>({});
  const [repOpen, setRepOpen] = useState(false);
  const [repWho, setRepWho] = useState('');
  const [repWhat, setRepWhat] = useState('');
  const [repKind, setRepKind] = useState('abuse');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  // Resolving an emergency asks what happened (the Flutter console's resolve note and
  // false-alarm choice); nothing is sent with a fixed resolution text.
  const [resolveFor, setResolveFor] = useState<Case | null>(null);
  // Acknowledging has a 10-second Undo and no confirm (AF-1611): it is routine and speed
  // matters. Console V6 draws it on the emergency card itself ("Undo · 9s").
  const [ackUndo, setAckUndo] = useState<{ id: string; until: number } | null>(null);
  useEffect(() => {
    if (!ackUndo) return;
    const t = window.setTimeout(() => setAckUndo(null), Math.max(0, ackUndo.until - Date.now()));
    return () => window.clearTimeout(t);
  }, [ackUndo]);
  // An emergency step that did not go through (V6): Aithora did not answer, so nothing
  // changed; the card says so with Try again. A refusal (409) shows the server's own words.
  const [emFail, setEmFail] = useState<{ id: string; what: string; retry: () => void } | null>(null);
  const [emMsg, setEmMsg] = useState<{ id: string; text: string } | null>(null);
  const [falseAlarm, setFalseAlarm] = useState(false);
  const roster = load((c, signal) => residentsRoster(c, { signal }), { enabled: repOpen && lens !== 'ro' });

  const a = assignment.data?.assignment ?? null;
  const mine = useMemo(() => (staff && a ? new Set(a.residents.map((r) => r.id)) : null), [staff, a]);
  const orgName = session?.user.orgName ?? 'Your building';

  const g = useMemo(
    () =>
      buildGuardian({
        lens,
        meId: session?.user.id ?? null,
        now,
        signals: signals.data?.signals ?? [],
        incidents: incidents.data?.incidents ?? [],
        watch: watch.data?.items ?? [],
        tape: tape.data?.events ?? [],
        mine,
        orgName,
        filter,
        sel: selId,
        formFor,
      }),
    [lens, session?.user.id, now, signals.data, incidents.data, watch.data, tape.data, mine, orgName, filter, selId, formFor],
  );

  const reloadAll = () => {
    signals.reload();
    incidents.reload();
    tape.reload();
    // the side nav's Guardian badge counts the same cases
    badges.reload();
  };

  /** Run one write, then re-read the cases. Refused while read only (the client refuses too). */
  const act = async (fn: () => Promise<unknown>) => {
    if (readOnly || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reloadAll();
    } catch (e) {
      setActionError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  /** One step on an emergency card: re-read on success; a failure stays on that card. */
  const runEm = async (x: Case, what: string, fn: () => Promise<unknown>): Promise<void> => {
    if (readOnly || busy) return;
    setBusy(true);
    setEmFail(null);
    setEmMsg(null);
    setActionError(null);
    try {
      await fn();
      reloadAll();
    } catch (e) {
      const err = e as ApiError;
      if (err && err.retryable) setEmFail({ id: x.id, what, retry: () => void runEm(x, what, fn) });
      else setEmMsg({ id: x.id, text: err?.message || 'That did not go through.' });
    } finally {
      setBusy(false);
    }
  };

  /** What each action on the emergency card does. */
  const emDo = (x: Case, a: EmAct) => {
    const body = { incidentId: x.id };
    // Acting on it opens it in the case panel (design upd: gSel = id).
    setSel(x.id);
    if (a.key === 'next') return void advance(x);
    if (a.key === 'too') return void runEm(x, EM.too, () => emergencyAcknowledge(client, { body }));
    if (a.key === 'c911') return void runEm(x, EM.c911, () => emergencyCalled911(client, { body }));
    // The server answers 409 'Record "Called 911" first...' until 911 is recorded: shown as it says.
    if (a.key === 'ems') return void runEm(x, EM.ems, () => emergencyEmsArrived(client, { body }));
    if (a.key === 'undo') {
      if (readOnly) return;
      setAckUndo(null);
      // Sent even while another action is running: the window is 10 seconds, and a lost Undo
      // must never vanish without a word.
      setEmFail(null);
      setEmMsg(null);
      void emergencyAcknowledgeUndo(client, { body })
        .then(() => reloadAll())
        .catch((e) => setEmMsg({ id: x.id, text: (e as ApiError)?.message || 'The Undo did not go through.' }));
    }
  };

  /** The design's advance(x): the next step on this case's family. */
  const advance = (x: Case) => {
    if (!g.canAct(x)) return;
    setSel(x.id);
    const f = familyOf(x);
    if (f === 'report' && x.stage === 'ack') {
      setFormFor(x.id);
      return;
    }
    if (x.src === 'incident') {
      const body = { incidentId: x.id };
      if (x.stage === 'new')
        return runEm(x, EM.going, async () => {
          const r = await emergencyAcknowledge(client, { body });
          // Only the first responder can take it back; a second responder is recorded too (AF-1616).
          if (r?.ok && r.first !== false && !r.again) setAckUndo({ id: x.id, until: Date.now() + EM.undoSeconds * 1000 });
        });
      if (x.stage === 'claimed') return runEm(x, EM.with, () => emergencyArrived(client, { body }));
      if (x.stage === 'onscene') {
        if (readOnly || busy) return;
        setFalseAlarm(false);
        setResolveFor(x);
        return;
      }
      return;
    }
    const step = (NEXT_STEP[f] ?? {})[x.stage];
    if (!step) return;
    return act(() => guardianStep(client, { params: { id: x.id }, body: { step } }));
  };

  const recips = recipientsFor(g.sel);
  const sbiVal = formSbi || (g.sel && g.sel.sbi === false ? 'no' : '');
  const formOk = !!sbiVal && recips.every((r) => (formRefs[r[0]] || '').trim());
  const fileReport = () => {
    const x = g.sel;
    if (!x || !formOk) return;
    const refs = recips.map((r) => `${r[1]} #${formRefs[r[0]].trim()}`).join(', ');
    return act(async () => {
      await guardianStep(client, { params: { id: x.id }, body: { step: 'report_filed', note: `Reported: ${refs}`, sbi: sbiVal === 'no' ? false : true } });
      setFormFor(null);
      setFormSbi('');
      setFormRefs({});
    });
  };

  const repOk = !!(repWho && repWhat.trim());
  const submitReport = () => {
    if (!repOk) return;
    return act(async () => {
      // A staff report opens confirmed and owned by the reporter: the person forming the
      // suspicion owns it from the first second (design gRepSubmit).
      const r = await guardianOpenSignal(client, { body: { ...reportBody(repKind), residentId: repWho, sbi: null, source: 'staff', note: repWhat.trim() } });
      setRepOpen(false);
      setRepWho('');
      setRepWhat('');
      setSel(r.signal.id);
      setFilter('open');
    });
  };

  if (signals.loading && !signals.data && !signals.error) {
    return (
      <div className="gd" data-testid="guardian">
        <Skeleton rows={6} label="Loading Guardian" />
      </div>
    );
  }

  // A pick with nothing in it on this page (design geoLayer): say so, never an empty board.
  const loaded = !!signals.data && !incidents.loading && !watch.loading;
  if (facilities && loaded && !signals.data!.signals.length && !(incidents.data?.incidents.length) && !(watch.data?.items.length)) {
    return (
      <div className="gd" data-testid="guardian">
        <ScopeEmpty title={nothingFrom} body="No emergencies, cases or early warnings from the buildings in this view. Guardian lists them here as soon as one comes in." />
      </div>
    );
  }

  const scope = g.scope ?? `${a?.unit ?? 'Your residents'} · ${orgName}`;
  // V9 medicine alerts: open Guardian cases a reported medicine-watch sign opened (refs med_watch:).
  const medAlerts = medicineAlerts(signals.data?.signals ?? [], { canAct: lens !== 'ro', meId: session?.user.id ?? null });
  // Wandering watch and alarm noise (V9): no feed yet, so neither adds to the answer.
  const answerPre = '';
  const answerAlso = '';
  const feeds = guardianFeeds({ lens, roleLabel: tierLabel(role) });
  const sel = g.sel;
  const selC = g.selClock;
  const rowKey = (id: string) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSel(id);
    }
  };
  const repKinds = REPORT_KINDS.map(([k, label]) => ({
    k,
    label,
    style:
      'display:inline-flex;align-items:center;min-height:' + (staff ? 44 : 36) + 'px;padding:0 14px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
      (repKind === k ? 'color:#FCFAF4;background:#22272E;' : 'color:#22272E;background:#fff;border:1px solid rgba(34,39,46,.2);'),
  }));
  const repSubmitStyle = btn(staff, repOk ? 'red' : 'ghost') + (repOk ? '' : 'opacity:.55;pointer-events:none;');
  const formSubmitStyle = btn(staff, formOk ? 'red' : 'ghost') + (formOk ? '' : 'opacity:.55;pointer-events:none;');
  const writeOff = readOnly || busy;
  const meId = session?.user.id ?? null;

  /** The emergency card's lines, 911 steps, actions and failure (V6 emView): top banner or case panel. */
  const emCardBody = (x: Case, top: boolean) => {
    const undoLeft = ackUndo && ackUndo.id === x.id ? Math.max(0, Math.ceil((ackUndo.until - now) / 1000)) : 0;
    const v = emergencyCard(x, top, { meId, canAct: g.canAct(x), undoLeft });
    const P = 'display:inline-flex;align-items:center;min-height:48px;padding:0 22px;border-radius:10px;font-size:16px;font-weight:800;text-decoration:none;white-space:nowrap;color:#fff;background:#B3261E;';
    const S = 'display:inline-flex;align-items:center;min-height:48px;padding:0 18px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#8A1C16;background:#fff;border:1px solid rgba(179,38,30,.4);';
    const lineStyle = (strong: boolean) => (strong ? 'display:block;font-size:16px;font-weight:700;color:#22272E;' : 'display:block;font-size:16px;color:#54606C;line-height:1.45;');
    const fail = emFail && emFail.id === x.id ? emFail : null;
    const msg = emMsg && emMsg.id === x.id ? emMsg.text : null;
    const failBox = fail && (
      <span role="alert" style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:10px 12px;border-radius:9px;' + (top ? 'background:#fff;border:1px solid rgba(179,38,30,.4);' : 'background:rgba(179,38,30,.08);border:1px solid rgba(179,38,30,.3);'))}>
        <span style={sx('flex:1 1 220px;min-width:0;font-size:16px;line-height:1.4;color:#22272E;')}>
          <b>{`“${fail.what}” didn’t go through.`}</b> {EM.failWhy} Call the nurse station if this can’t wait.
        </span>
        <A style="flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:8px;background:#22272E;color:#fff;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => fail.retry()}>
          Try again
        </A>
      </span>
    );
    const msgBox = msg && (
      <span role="alert" style={sx('display:block;padding:10px 12px;border-radius:9px;font-size:16px;line-height:1.4;color:#22272E;' + (top ? 'background:#fff;border:1px solid rgba(179,38,30,.4);' : 'background:rgba(179,38,30,.08);border:1px solid rgba(179,38,30,.3);'))}>{msg}</span>
    );
    const acts = v.acts.length > 0 && (
      <span style={sx(top ? 'display:flex;gap:8px;flex-wrap:wrap;' : 'display:flex;gap:8px;flex-wrap:wrap;margin-top:4px;')}>
        {v.acts.map((a) => (
          <A key={a.key} style={a.primary ? P : S} onClick={() => emDo(x, a)} disabled={a.key === 'undo' ? readOnly : writeOff} title={roReason ?? undefined}>
            {a.label}
          </A>
        ))}
      </span>
    );
    if (top) {
      return (
        <>
          {v.lines.length > 0 && (
            <span style={sx('display:flex;flex-direction:column;gap:3px;')}>
              {v.lines.map((l, i) => (
                <span key={i} style={sx(lineStyle(l.strong))}>{l.t}</span>
              ))}
            </span>
          )}
          {v.steps.length > 0 && (
            <span style={sx('display:flex;gap:6px 16px;flex-wrap:wrap;')}>
              {v.steps.map((z) => (
                <span key={z.t} style={sx('display:inline-flex;align-items:baseline;gap:8px;font-size:16px;')}>
                  <span style={sx('font-weight:800;color:#8A1C16;')}>{z.t}</span>
                  <span style={sx('color:#3C454F;')}>{z.w}</span>
                </span>
              ))}
            </span>
          )}
          {acts || <span style={sx('display:flex;gap:8px;flex-wrap:wrap;')} />}
          {failBox}
          {msgBox}
        </>
      );
    }
    return (
      <>
        {v.lines.map((l, i) => (
          <div key={i} style={sx(lineStyle(l.strong))}>{l.t}</div>
        ))}
        {v.steps.length > 0 && (
          <div style={sx('display:flex;flex-direction:column;gap:2px;margin-top:4px;')}>
            {v.steps.map((z) => (
              <div key={z.t} style={sx('display:flex;align-items:baseline;gap:10px;font-size:16px;')}>
                <span style={sx('font-weight:700;color:#8F362F;')}>{z.t}</span>
                <span style={sx('color:#54606C;')}>{z.w}</span>
              </div>
            ))}
          </div>
        )}
        {acts}
        {failBox && <div>{failBox}</div>}
        {msgBox && <div>{msgBox}</div>}
      </>
    );
  };

  return (
    <div className="gd" data-testid="guardian" style={sx('flex:1 1 auto;display:flex;flex-direction:column;gap:14px;min-height:0;min-width:0;overflow-y:auto;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;min-width:0;')}>
        <h1 style={sx('margin:0;flex:0 0 auto;font-size:20px;font-weight:800;letter-spacing:-.022em;line-height:1;')}>Guardian</h1>
        <span style={sx('flex:1 1 160px;min-width:0;font-size:16px;color:#54606C;')}>
          {scope}
          {signals.data && <HonestyBadge flags={[signals.data]} figure="Guardian signals" hide={['MEASURED', 'MODELED', 'ESTIMATED']} />}
        </span>
        {g.canReport && (
          <A
            style="flex:0 0 auto;display:inline-flex;align-items:center;gap:8px;min-height:40px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;background:rgba(179,38,30,.09);border:1px solid rgba(179,38,30,.28);"
            onClick={() => setRepOpen((o) => !o)}
          >
            Report a concern
          </A>
        )}
      </div>

      {medAlerts.map((m) => (
        <div key={m.id} data-screen-label="Guardian · Medicine alert" role="alert" data-testid="guardian-medicine-alert" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding:12px 14px;border-radius:11px;background:#FBEDEC;border:1px solid #B3261E;min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;')}>
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#8A1C16;')}>Medicine alert</span>
            <span style={sx('flex:1 1 300px;min-width:0;font-size:18px;font-weight:800;color:#8A1C16;line-height:1.3;')}><span>{m.head}</span></span>
          </div>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}><span>{m.sub}</span></span>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            {m.unclaimed && (
              <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;background:#B3261E;color:#fff;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => void act(() => guardianStep(client, { params: { id: m.id }, body: { step: 'claim' } }))} disabled={writeOff} title={roReason ?? undefined}>
                I{'’'}m on it
              </A>
            )}
            {m.claimed && (
              <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;" onClick={() => void act(() => guardianStep(client, { params: { id: m.id }, body: { step: 'closed', note: 'Medicine alert checked and closed' } }))} disabled={writeOff} title={roReason ?? undefined}>
                Done, close alert
              </A>
            )}
          </div>
        </div>
      ))}

      <div style={sx('flex:0 0 auto;display:flex;align-items:flex-end;gap:10px 28px;flex-wrap:wrap;min-width:0;')}>
        <div style={sx('flex:1 1 380px;min-width:0;')}>
          <div style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>Is anyone in danger right now?</div>
          <div data-testid="guardian-answer" style={sx(`font-size:27px;font-weight:750;letter-spacing:-.025em;line-height:1.2;margin-top:5px;text-wrap:pretty;color:${g.answerColor};`)}>{g.answer}</div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;margin-top:4px;text-wrap:pretty;')}><span>{answerPre}</span><span>{g.answerSub}</span> <span>{answerAlso}</span></div>
        </div>
        <div style={sx('flex:0 1 auto;display:flex;gap:6px 22px;flex-wrap:wrap;')}>
          {g.stats.map((x) => (
            <span key={x.k} style={sx('display:flex;flex-direction:column;gap:2px;min-width:0;')}>
              <span style={sx(`font-size:22px;font-weight:750;letter-spacing:-.02em;line-height:1;font-variant-numeric:tabular-nums;color:${x.c};`)}>{x.v}</span>
              <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{x.k}</span>
            </span>
          ))}
        </div>
      </div>

      {actionError && <ErrorState error={actionError} />}
      {signals.error && <ErrorState error={signals.error} onRetry={signals.reload} />}

      {g.emShow && g.em && (
        <div role="alert" data-testid="guardian-emergency" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:12px 20px;flex-wrap:wrap;padding:16px 18px;border-radius:14px;background:#FBEDEC;border:2px solid #B3261E;min-width:0;')}>
          <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:2px;')}>
            <span style={sx('display:inline-flex;align-items:center;gap:7px;font-size:14px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#B3261E;')}>
              <span style={sx('width:8px;height:8px;border-radius:50%;background:#B3261E;animation:coPulse 1.4s ease-out infinite;')} />
              Emergency
            </span>
            <span style={sx('font-size:30px;font-weight:800;letter-spacing:-.02em;line-height:1;font-variant-numeric:tabular-nums;color:#8A1C16;')}>{g.emClock}</span>
          </span>
          <span style={sx('flex:1 1 300px;min-width:0;')}>
            <span style={sx('display:block;font-size:17px;font-weight:750;color:#22272E;')}>
              <span>{g.emWho}</span> &middot; <span>{g.emState}</span>
            </span>
            <span style={sx('display:block;font-size:16px;color:#3C454F;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>{g.emWhat}</span>
          </span>
          {g.emCanAct && (
            <span style={sx('flex:1 1 100%;display:flex;flex-direction:column;gap:8px;min-width:0;')}>{emCardBody(g.em, true)}</span>
          )}
        </div>
      )}

      {repOpen && g.canReport && (
        <div data-testid="guardian-report" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:12px;padding:18px;border-radius:14px;background:#fff;border:1px solid rgba(34,39,46,.16);min-width:0;')}>
          <div style={sx('display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;')}>
            <span style={sx('font-size:18px;font-weight:750;letter-spacing:-.015em;')}>Report a concern</span>
            <span style={sx('flex:1 1 280px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
              Your clock starts now: 2 hours until someone confirms there was no serious injury, then 24. You can file straight away.
            </span>
          </div>
          <div style={sx('display:flex;gap:10px;flex-wrap:wrap;min-width:0;')}>
            <select
              value={repWho}
              onChange={(e) => setRepWho(e.target.value)}
              aria-label="Resident"
              style={sx('flex:0 1 260px;min-width:200px;min-height:44px;padding:0 12px;border-radius:9px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;font-size:16px;font-family:inherit;color:#22272E;')}
            >
              <option value="">Choose a resident</option>
              {(roster.data?.residents ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name ?? r.preferred_name ?? 'Resident'}
                </option>
              ))}
            </select>
            {/* One choice, already picked (Abuse by default): a radio group, so the current pick
                reads as chosen and pressing it again is not a dead button. */}
            <span role="radiogroup" aria-label="What kind of concern" data-testid="report-kinds" style={sx('flex:1 1 300px;display:flex;gap:6px;flex-wrap:wrap;min-width:0;')}>
              {repKinds.map((k) => (
                <button
                  key={k.k}
                  type="button"
                  role="radio"
                  aria-checked={repKind === k.k}
                  className="gd-a"
                  style={sx(k.style + (repKind === k.k ? 'cursor:default;' : ''))}
                  onClick={() => setRepKind(k.k)}
                >
                  {k.label}
                </button>
              ))}
            </span>
          </div>
          <textarea
            value={repWhat}
            onChange={(e) => setRepWhat(e.target.value)}
            rows={3}
            placeholder="What did you see or hear? Plain words, as you would tell the nurse."
            aria-label="What you saw"
            style={sx('box-sizing:border-box;width:100%;min-height:84px;padding:12px 14px;border-radius:10px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;font-size:16px;line-height:1.5;font-family:inherit;color:#22272E;resize:vertical;')}
          />
          {roster.error && <ErrorState error={roster.error} onRetry={roster.reload} />}
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            <A style={repSubmitStyle} onClick={submitReport} disabled={!repOk || writeOff} title={roReason ?? undefined}>
              Report it
            </A>
            <A style="display:inline-flex;align-items:center;min-height:40px;padding:0 14px;font-size:16px;font-weight:700;text-decoration:none;color:#3C454F;" onClick={() => setRepOpen(false)}>
              Cancel
            </A>
          </div>
        </div>
      )}

      <GuardianFeedsView V={feeds} />

      <GuardianApprovals />

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:14px;min-width:0;padding:9px 0;border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);overflow:hidden;')}>
        <span style={sx('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;')}>Safety tape</span>
        <span style={sx('flex:1 1 auto;min-width:0;display:flex;gap:22px;overflow-x:auto;scrollbar-width:none;')}>
          {g.tape.length === 0 && <span style={sx('flex:0 0 auto;font-size:16px;color:#54606C;white-space:nowrap;')}>Nothing on the tape yet.</span>}
          {g.tape.map((t, i) => (
            <span key={i} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;gap:7px;white-space:nowrap;font-size:16px;')}>
              <span style={sx(t.dot)} />
              <span style={sx('font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;')}>{t.t}</span>
              <span style={sx('color:#54606C;')}>{t.k}</span>
              <span style={sx('color:#22272E;')}>{t.d}</span>
            </span>
          ))}
        </span>
      </div>

      <div style={sx('flex:1 0 auto;display:flex;gap:18px 22px;flex-wrap:wrap;align-items:flex-start;min-width:0;')}>
        <div style={sx('flex:1.25 1 420px;min-width:0;display:flex;flex-direction:column;gap:10px;')}>
          <div role="tablist" aria-label="Guardian cases" style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
            {g.filters.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                className="gd-a"
                style={sx(f.style)}
                onClick={() => {
                  setFilter(f.key);
                  // A failed action's error belongs to its tab, not to every tab after it (QA-21).
                  setActionError(null);
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          {g.empty && <div style={sx('padding:26px 4px;font-size:16px;color:#3C454F;line-height:1.5;max-width:60ch;text-wrap:pretty;')}>{g.emptyText}</div>}
          {g.rows.map((r) => (
            <div key={r.id} role="button" tabIndex={0} aria-pressed={sel?.id === r.id} data-testid={`guardian-row-${r.id}`} onClick={() => setSel(r.id)} onKeyDown={rowKey(r.id)} style={sx(r.rowStyle)}>
              <span style={sx(r.edgeStyle)} />
              <div style={sx('flex:1 1 auto;min-width:0;padding:12px 14px 12px 13px;display:flex;flex-direction:column;gap:6px;')}>
                <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;min-width:0;')}>
                  <span style={sx('flex:0 1 auto;min-width:0;font-size:15.5px;font-weight:750;letter-spacing:-.01em;')}>{r.who}</span>
                  <span style={sx('flex:0 0 auto;font-size:16px;color:#54606C;')}>{r.room}</span>
                  <span style={sx('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#54606C;')}>{r.kind}</span>
                  <span style={sx('flex:1 1 20px;')} />
                  <span style={sx(`flex:0 0 auto;font-size:16px;font-weight:750;font-variant-numeric:tabular-nums;white-space:nowrap;color:${r.clockColor};`)}>{r.clockText}</span>
                </div>
                <span style={sx('display:block;height:3px;border-radius:2px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                  <span style={sx(r.barStyle)} />
                </span>
                <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;text-wrap:pretty;white-space:normal;overflow-wrap:break-word;')}>{r.what}</div>
                <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:nowrap;white-space:nowrap;min-width:0;')}>
                  <span style={sx(`flex:0 0 auto;font-size:16px;font-weight:700;color:${r.ownerColor};`)}>{r.owner}</span>
                  <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;gap:3px;')}>
                    {r.dots.map((d, i) => (
                      <span key={i} style={sx(d)} />
                    ))}
                  </span>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#54606C;white-space:normal;overflow-wrap:break-word;')}>{r.stageLabel}</span>
                  {r.hasNext && (
                    <A
                      style={r.nextStyle}
                      onClick={() => {
                        const x = g.sorted.find((y) => y.id === r.id);
                        if (x) void advance(x);
                      }}
                      disabled={writeOff}
                      title={roReason ?? undefined}
                    >
                      {r.nextLabel}
                    </A>
                  )}
                </div>
              </div>
            </div>
          ))}
          {g.hidden > 0 && <div style={sx('font-size:16px;color:#54606C;padding:2px 4px;')}>{`${g.hidden} more on other floors are with their own teams.`}</div>}
        </div>

        {sel && selC && (
          <div data-testid="guardian-detail" style={sx('flex:1 1 360px;min-width:0;display:flex;flex-direction:column;gap:16px;padding:18px 20px;border-radius:14px;background:#fff;box-shadow:0 0 0 1px rgba(34,39,46,.12);')}>
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:21px;font-weight:800;letter-spacing:-.02em;line-height:1.2;')}>{sel.who}</div>
              <div style={sx('font-size:16px;color:#54606C;margin-top:3px;')}>{g.selMeta}</div>
              <div style={sx('font-size:16px;color:#22272E;line-height:1.5;margin-top:10px;text-wrap:pretty;')}>{sel.what}</div>
            </div>
            <div style={sx('display:flex;gap:6px;min-width:0;')}>
              {g.selStages.map((z) => (
                <span key={z.label} style={sx(z.s)}>
                  <span style={sx(z.bar)} />
                  {z.label}
                </span>
              ))}
            </div>
            <div style={sx('min-width:0;padding:14px;border-radius:11px;background:#FCFAF4;border:1px solid rgba(34,39,46,.1);')}>
              <div style={sx(`font-size:22px;font-weight:800;letter-spacing:-.02em;line-height:1.1;font-variant-numeric:tabular-nums;color:${selC.color};`)}>{selC.text}</div>
              <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:9px;')}>
                <span style={sx(g.selBar)} />
              </span>
              <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;margin-top:8px;text-wrap:pretty;')}>{selC.basis}</div>
            </div>
            <div style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;min-width:0;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#54606C;')}>Owner</span>
              <span style={sx(`font-size:16px;font-weight:700;color:${g.selOwnerColor};`)}>{g.selOwner}</span>
              {g.canAssign && (
                <select
                  value={sel.ownerId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v || sel.src === 'incident') return;
                    const body = { ownerUserId: v };
                    void act(() => (sel.src === 'watch' ? guardianWatchAssign(client, { params: { id: sel.id }, body }) : guardianAssign(client, { params: { id: sel.id }, body })));
                  }}
                  aria-label="Assign owner"
                  disabled={writeOff || sel.src === 'incident'}
                  title={sel.src === 'incident' ? 'Whoever says “I’m going” owns an emergency.' : (roReason ?? undefined)}
                  style={sx('min-height:36px;padding:0 10px;border-radius:8px;border:1px solid rgba(34,39,46,.22);background:#FCFAF4;font-size:16px;font-family:inherit;color:#22272E;')}
                >
                  <option value="">Assign to&hellip;</option>
                  {(assignable.data?.people ?? []).map((p) => (
                    <option key={p.userId} value={p.userId}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {g.selHasNext && !(sel.src === 'incident' && sel.stage !== 'new' && !(sel.responders ?? []).some((r) => r.id === meId)) && (
              <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
                <A style={g.selNextStyle} onClick={() => void advance(sel)} disabled={writeOff} title={roReason ?? undefined}>
                  {g.selNext}
                </A>
                <A
                  style={g.selEscStyle}
                  onClick={() => {
                    if (sel.src !== 'signal' || sel.escalated) return;
                    void act(() => guardianStep(client, { params: { id: sel.id }, body: { step: 'escalated' } }));
                  }}
                  disabled={writeOff || sel.src !== 'signal'}
                  title={roReason ?? (sel.src !== 'signal' ? 'An emergency escalates on its own clock.' : undefined)}
                >
                  {g.selEscLabel}
                </A>
              </div>
            )}
            {sel.src === 'incident' && sel.stage !== 'closed' && (
              <div data-screen-label="Emergency responders" data-testid="guardian-responders" style={sx('display:flex;flex-direction:column;gap:6px;padding:12px 0;border-top:1px solid rgba(34,39,46,.1);')}>{emCardBody(sel, false)}</div>
            )}
            {sel.src === 'incident' && (
              <EmergencyExtras client={client} sel={sel} canAct={g.canAct(sel) && sel.stage !== 'closed'} disabled={writeOff} title={roReason ?? undefined} />
            )}
            {g.selRo && (
              <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;padding:10px 12px;border-radius:9px;background:rgba(34,39,46,.05);text-wrap:pretty;')}>
                Read-only as {tierLabel(role)}. The building&rsquo;s staff own this; you see the clock and every step they take.
              </div>
            )}
            {g.formShow && (
              <div data-testid="guardian-file" style={sx('display:flex;flex-direction:column;gap:12px;padding:16px;border-radius:12px;border:2px solid #B3261E;background:#FFF8F7;min-width:0;')}>
                <div style={sx('font-size:17px;font-weight:800;letter-spacing:-.01em;')}>File the report</div>
                <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Did it cause serious bodily injury?</div>
                <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
                  {(
                    [
                      ['yes', 'Yes', '2 hours'],
                      ['no', 'No', '24 hours'],
                      ['unsure', 'Not sure', 'treated as 2 hours'],
                    ] as ['yes' | 'no' | 'unsure', string, string][]
                  ).map(([k, label, sub]) => (
                    <A
                      key={k}
                      style={
                        'flex:1 1 110px;display:flex;flex-direction:column;align-items:flex-start;gap:2px;min-height:' + (staff ? 56 : 48) + 'px;justify-content:center;padding:0 13px;border-radius:9px;text-decoration:none;box-sizing:border-box;' +
                        (sbiVal === k ? 'color:#FCFAF4;background:#22272E;' : 'color:#22272E;background:#fff;border:1px solid rgba(34,39,46,.2);')
                      }
                      onClick={() => setFormSbi(k)}
                    >
                      <span style={sx('font-size:16px;font-weight:750;')}>{label}</span>
                      <span style={sx('font-size:14px;opacity:.8;')}>{sub}</span>
                    </A>
                  ))}
                </div>
                {recips.map(([k, label, sub]) => (
                  <label key={k} style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;min-width:0;')}>
                    <span style={sx('flex:1 1 200px;min-width:0;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:700;')}>{label}</span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;')}>{sub}</span>
                    </span>
                    <input
                      type="text"
                      value={formRefs[k] ?? ''}
                      onChange={(e) => setFormRefs((p) => ({ ...p, [k]: e.target.value }))}
                      placeholder="Their reference number"
                      style={sx('box-sizing:border-box;flex:1 1 180px;min-width:160px;min-height:40px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.22);background:#fff;font-size:16px;font-family:inherit;color:#22272E;')}
                    />
                  </label>
                ))}
                <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
                  {sel.type === 'SNF' ? 'Federal law needs both. A report to one does not cover the other.' : 'Assisted living follows the state rule, not the federal one.'} Filing writes a sealed entry to the record with your name and the time.
                </div>
                <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
                  <A style={formSubmitStyle} onClick={() => void fileReport()} disabled={!formOk || writeOff} title={roReason ?? undefined}>
                    Mark as reported
                  </A>
                  <A style="display:inline-flex;align-items:center;min-height:36px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;color:#3C454F;" onClick={() => setFormFor(null)}>
                    Not yet
                  </A>
                </div>
              </div>
            )}
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;padding-bottom:6px;')}>Every step, as recorded</div>
              {g.selLog.map((l, i) => (
                <div key={i} style={sx('display:grid;grid-template-columns:62px minmax(0,1fr) auto;gap:10px;align-items:baseline;padding:8px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                  <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;')}>{l.t}</span>
                  <span style={sx('min-width:0;')}>
                    <span style={sx('display:block;font-size:16px;color:#22272E;line-height:1.4;text-wrap:pretty;')}>{l.what}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;')}>{l.who}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{l.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {resolveFor && (
        <ConfirmDialog
          title={`Resolve ${resolveFor.who}’s ${resolveFor.emWord || 'emergency'}?`}
          body={
            <>
              <span style={sx('display:block;')}>Write what happened and what was done. It is sealed to the record with your name and the time.</span>
              <label style={sx('display:flex;align-items:center;gap:8px;margin-top:10px;font-size:16px;color:#22272E;cursor:pointer;')}>
                <input type="checkbox" checked={falseAlarm} onChange={(e) => setFalseAlarm(e.target.checked)} />
                It was a false alarm
              </label>
            </>
          }
          confirmLabel="Resolve"
          reason={{ label: 'What happened', placeholder: 'What happened and what was done (required)', required: true }}
          onConfirm={async (note) => {
            const x = resolveFor;
            setBusy(true);
            setActionError(null);
            try {
              await emergencyResolve(client, { body: { incidentId: x.id, resolution: note, resolutionType: falseAlarm ? 'false_positive' : 'mitigated' } });
              reloadAll();
            } finally {
              setBusy(false);
            }
          }}
          onClose={() => setResolveFor(null)}
        />
      )}
    </div>
  );
}
